/** Generic catalog page — reused by Courses, Sadhana, Products, Services, Research, Library. */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Container, Section, Eyebrow, PageHeader, AccentItalic, Breadcrumb,
  Card, CardMedia, CardBody, CardTitle, CardDesc, CardFoot, CardTag,
  Badge, RatingStars, LevelBadge, PriceBlock, SearchInput, Pagination,
  ArtCard, CardSkeleton, EmptyState, Button,
} from "../../components/ui";
import { api, formatApiErrorDetail } from "../../lib/api";

export function CatalogPage({
  title, eyebrow, lede, kind, listPath, categoriesPath, detailPath,
  showLevel = false, showPrice = true, showRating = true,
}) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [err, setErr] = useState(null);
  const page = parseInt(params.get("page") || "1", 10);
  const q = params.get("q") || "";
  const cat = params.get("cat") || "";
  const level = params.get("level") || "";
  const type = params.get("type") || "";

  const setQuery = (next) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
    setParams(p);
  };

  useEffect(() => { if (categoriesPath) api.get(categoriesPath).then((r) => setCats(r.data.items || [])).catch(() => {}); }, [categoriesPath]);

  useEffect(() => {
    setData(null); setErr(null);
    const qs = new URLSearchParams({ page, limit: 24 });
    if (q) qs.set("q", q);
    if (cat) qs.set("category", cat);
    if (level) qs.set("level", level);
    if (type) qs.set("type", type);
    let stale = false;
    api.get(`${listPath}?${qs.toString()}`)
      .then((r) => { if (!stale) setData(r.data); })
      .catch((e) => { if (!stale) setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); });
    return () => { stale = true; };
  }, [listPath, page, q, cat, level, type]);

  return (
    <Section padded="sm">
      <Container>
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: title }]} />
        <PageHeader eyebrow={eyebrow} title={<>{title.split(" ").slice(0, -1).join(" ")} <AccentItalic>{title.split(" ").slice(-1)[0]}</AccentItalic></>} lede={lede} className="mt-6" />

        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-8" data-testid={`catalog-filters-${kind}`}>
          <SearchInput value={q} onChange={(e) => setQuery({ q: e.target.value, page: 1 })} placeholder="Search…" className="md:max-w-[360px] flex-1" testId={`catalog-search-${kind}`} />
          {cats.length > 0 && (
            <select value={cats.find((c) => c.slug === cat)?.id || cat} onChange={(e) => setQuery({ cat: e.target.value, page: 1 })} data-testid={`catalog-category-${kind}`}
              className="bg-surface border border-line-2 text-text rounded-full px-4 py-2.5 text-[13px] focus:outline-none focus:border-mauve">
              <option value="">All categories</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {showLevel && (
            <select value={level} onChange={(e) => setQuery({ level: e.target.value, page: 1 })} data-testid={`catalog-level-${kind}`}
              className="bg-surface border border-line-2 text-text rounded-full px-4 py-2.5 text-[13px] focus:outline-none focus:border-mauve">
              <option value="">All levels</option>
              <option value="foundational">Foundational</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          )}
        </div>

        {err && <EmptyState title="Couldn't load" description={err} />}
        {!err && !data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        )}
        {data && data.items.length === 0 && (
          <EmptyState title="Nothing here yet" description="Try adjusting your filters or check back soon." />
        )}
        {data && data.items.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`catalog-grid-${kind}`}>
              {data.items.map((it, idx) => (
                <Link key={it.id} to={detailPath.replace(":slug", it.slug)} data-testid={`catalog-item-${kind}-${it.slug}`}>
                  <Card interactive className="h-full">
                    <CardMedia>
                      {it.hero_image_url ? (
                        <img src={it.hero_image_url} alt={it.title}
                             className="absolute inset-0 w-full h-full object-cover"
                             onError={(e) => { e.currentTarget.style.display = "none"; }}
                             data-testid={`catalog-image-${kind}-${it.slug}`} />
                      ) : (
                        <ArtCard seed={(idx + (it.title?.length || 0)) % 5} motifSeed={(idx * 3) % 4} />
                      )}
                      {it.featured && <CardTag className="absolute top-3 left-3 !bg-glass-2">Featured</CardTag>}
                      {it.bestseller && <CardTag className="absolute top-3 right-3 !bg-glass-2 !text-gold !border-gold/40">Bestseller</CardTag>}
                    </CardMedia>
                    <CardBody>
                      {showLevel && it.level && <LevelBadge level={({ foundational: 1, intermediate: 2, advanced: 3 })[it.level] || 1} className="mb-2" />}
                      {it.deity && <Badge variant="info" className="mb-2">{it.deity}</Badge>}
                      {it.journal && <Badge variant="muted" className="mb-2">{it.journal}</Badge>}
                      <CardTitle className="mb-2">{it.title}</CardTitle>
                      <CardDesc className="line-clamp-2">{it.summary || it.tagline || it.abstract || ""}</CardDesc>
                      <CardFoot>
                        {showPrice && (it.is_free ? <Badge variant="success">Free</Badge> : <PriceBlock price={it.price_inr || 0} was={it.compare_at_inr} size="sm" />)}
                        {showRating && it.rating > 0 && <RatingStars value={it.rating} count={it.rating_count} />}
                      </CardFoot>
                    </CardBody>
                  </Card>
                </Link>
              ))}
            </div>
            {data.meta.page_count > 1 && (
              <div className="mt-12 flex justify-center">
                <Pagination page={data.meta.page} pageCount={data.meta.page_count} onPageChange={(p) => setQuery({ page: p })} />
              </div>
            )}
          </>
        )}
      </Container>
    </Section>
  );
}
